/**
 * useCapacityTelemetry.ts
 * Feature: 154 - Eventstream Real-Time Capacity Telemetry
 * Author: Peter Dobler
 * Date: 2026-05-18
 * Constitution Version: 1.12.0
 *
 * React Query hooks for capacity telemetry data.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { LiveQueryParams } from '../services/capacityTelemetryApi';
import {
  fetchLiveMetrics,
  fetchTelemetryStatus,
  fetchCapacityHistory,
  fetchForecast,
  fetchPipelines,
  fetchStreamingAlertRules,
} from '../services/capacityTelemetryApi';
import { useCapacityFilterStore } from '../stores/capacityFilterStore';
import { filterCapacities } from '../utils/capacityFilter';

export const telemetryKeys = {
  all: ['capacityTelemetry'] as const,
  live: (params?: LiveQueryParams) => [...telemetryKeys.all, 'live', params] as const,
  status: () => [...telemetryKeys.all, 'status'] as const,
  history: (capacityId: string, windowMinutes: number) =>
    [...telemetryKeys.all, 'history', capacityId, windowMinutes] as const,

  forecast: (capacityId: string) => [...telemetryKeys.all, 'forecast', capacityId] as const,
  pipelines: () => [...telemetryKeys.all, 'pipelines'] as const,
  alertRules: (capacityId?: string) =>
    [...telemetryKeys.all, 'alertRules', capacityId] as const,
};

// ---------------------------------------------------------------------------
// Live metrics — polls every 30s by default
// ---------------------------------------------------------------------------

export function useLiveMetrics(params?: LiveQueryParams, intervalMs = 30_000) {
  const { showPausedUnknown } = useCapacityFilterStore();
  return useQuery({
    queryKey: telemetryKeys.live(params),
    queryFn: () => fetchLiveMetrics(params),
    refetchInterval: intervalMs,
    staleTime: intervalMs / 2,
    select: (data) => ({
      ...data,
      capacities: filterCapacities(data.capacities, showPausedUnknown),
    }),
  });
}

// ---------------------------------------------------------------------------
// Service status — polls every 30s
// ---------------------------------------------------------------------------

export function useTelemetryStatus(intervalMs = 30_000) {
  return useQuery({
    queryKey: telemetryKeys.status(),
    queryFn: fetchTelemetryStatus,
    refetchInterval: intervalMs,
    staleTime: intervalMs / 2,
  });
}

// ---------------------------------------------------------------------------
// Capacity history — on-demand
// ---------------------------------------------------------------------------

export function useCapacityHistory(capacityId: string | undefined, windowMinutes = 30) {
  return useQuery({
    queryKey: telemetryKeys.history(capacityId ?? '', windowMinutes),
    queryFn: () => fetchCapacityHistory(capacityId!, windowMinutes),
    enabled: !!capacityId,
    staleTime: 30_000,
  });
}

// Portfolio hook retired (Spec 216 T040 — CapacityPortfolioView replaced by
// Capacity Insights, which has its own /api/capacity-insights payload).

// ---------------------------------------------------------------------------
// Forecast — on-demand, refetch when capacityId changes
// ---------------------------------------------------------------------------

export function useThrottlingForecast(capacityId: string | undefined) {
  return useQuery({
    queryKey: telemetryKeys.forecast(capacityId ?? ''),
    queryFn: () => fetchForecast(capacityId!),
    enabled: !!capacityId,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Admin: pipelines
// ---------------------------------------------------------------------------

export function usePipelines() {
  return useQuery({
    queryKey: telemetryKeys.pipelines(),
    queryFn: fetchPipelines,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Alert rules
// ---------------------------------------------------------------------------

export function useStreamingAlertRules(capacityId?: string) {
  return useQuery({
    queryKey: telemetryKeys.alertRules(capacityId),
    queryFn: () => fetchStreamingAlertRules(capacityId),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Invalidation helpers
// ---------------------------------------------------------------------------

export function useInvalidateTelemetry() {
  const queryClient = useQueryClient();
  return {
    invalidateLive: () => queryClient.invalidateQueries({ queryKey: telemetryKeys.live() }),
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: telemetryKeys.all }),
    invalidatePipelines: () =>
      queryClient.invalidateQueries({ queryKey: telemetryKeys.pipelines() }),
    invalidateAlertRules: () =>
      queryClient.invalidateQueries({ queryKey: telemetryKeys.alertRules() }),
  };
}
